import { API_CONFIG } from "../config/api.config";
import { apiRequest } from "./apiUtils";

export interface AdminUser {
  id: string;
  unionid: string;
  name?: string | null;
  avatar?: string | null;
  staffId?: number | null;
  createdAt: string;
}

export interface LoginResponse {
  success: boolean;
  message?: string;
  data?: {
    token: string;
    admin: AdminUser;
  };
}

/**
 * 管理员认证服务
 */
export class AuthService {
  private static instance: AuthService;
  private token: string | null = null;
  private admin: AdminUser | null = null;

  private constructor() {
    // 从本地存储恢复登录状态
    this.loadFromStorage();
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /**
   * 从本地存储加载认证信息
   */
  private loadFromStorage(): void {
    try {
      const token = localStorage.getItem("admin_token");
      const adminStr = localStorage.getItem("admin_user");

      if (token && adminStr) {
        this.token = token;
        this.admin = JSON.parse(adminStr);
      }
    } catch (error) {
      console.error("从本地存储加载认证信息失败:", error);
      this.clearAuth();
    }
  }

  /**
   * 微信登录
   */
  public async wechatLogin(code: string): Promise<LoginResponse> {
    try {
      const response = await apiRequest<LoginResponse>(
        API_CONFIG.ENDPOINTS.ADMIN_WECHAT_LOGIN,
        "POST",
        {
          code,
        },
        false // 登录请求不需要认证
      );

      if (response.success) {
        const { token, admin } = response.data!;
        this.setAuth(token, admin);
      }

      return response;
    } catch (error: any) {
      console.error("微信登录失败:", error);
      throw new Error(error.message || "微信登录失败");
    }
  }

  /**
   * 快速登录（开发环境）
   */
  public async quickLogin(unionid: string): Promise<LoginResponse> {
    try {
      const response = await apiRequest<LoginResponse>(
        API_CONFIG.ENDPOINTS.ADMIN_QUICK_LOGIN,
        "POST",
        {
          unionid,
        },
        false // 登录请求不需要认证
      );

      if (response.success) {
        const { token, admin } = response.data!;
        this.setAuth(token, admin);
      }

      return response;
    } catch (error: any) {
      console.error("快速登录失败:", error);
      throw new Error(error.message || "快速登录失败");
    }
  }

  /**
   * 设置认证信息
   */
  private setAuth(token: string, admin: AdminUser): void {
    this.token = token;
    this.admin = admin;

    // 保存到本地存储
    localStorage.setItem("admin_token", token);
    localStorage.setItem("admin_user", JSON.stringify(admin));
  }

  /**
   * 登出
   */
  public logout(): void {
    try {
      this.clearAuth();
    } catch (error) {
      console.error("登出清理过程中发生错误:", error);
      // 强制清理
      localStorage.removeItem("admin_token");
      this.token = null;
      this.admin = null;
    }
  }

  /**
   * 清除认证信息
   */
  private clearAuth(): void {
    this.token = null;
    this.admin = null;

    // 清除本地存储
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
  }

  /**
   * 检查是否已登录
   */
  public isAuthenticated(): boolean {
    return !!(this.token && this.admin);
  }

  /**
   * 获取当前管理员信息
   */
  public getCurrentAdmin(): AdminUser | null {
    console.log("getCurrentAdmin", this.admin);
    return this.admin;
  }

  /**
   * 获取当前token
   */
  public getToken(): string | null {
    return this.token;
  }

  /**
   * 验证token有效性
   */
  public async validateToken(): Promise<boolean> {
    if (!this.token) {
      return false;
    }

    try {
      const response = await apiRequest<{ success: boolean }>(
        API_CONFIG.ENDPOINTS.ADMIN_VALIDATE_TOKEN,
        "GET",
        null,
        false, // 不使用默认认证机制，通过options传递认证头
        {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        }
      );

      return response.success;
    } catch (error) {
      console.error("Token验证失败:", error);
      this.clearAuth();
      return false;
    }
  }
}

// 导出单例实例
export const authService = AuthService.getInstance();
